/* eslint-disable no-nested-ternary */
import { ChangeEvent } from 'react';
import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';
import { OutboundProcess } from '@features/supplyChain/types';
import { useReactHookFormWithFieldArray } from '@hooks';
import { useAuth } from '@context';
import { PalletCalculator } from '@components/PalletCalculator';
import { CreateProofOfDeliveryFormValues } from './types';
import { makeDefaultValues, validationSchema } from './form';
import { Datepicker } from '../../../tailwindComponents/Datepicker/Datepicker';
import { Input } from '../../../tailwindComponents/Input/Input';
import { Textarea } from '../../../tailwindComponents/Textarea/Textarea';
import { Button } from '../../../tailwindComponents/Button/Button';

function CopyIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='14' height='14' x='8' y='8' rx='2' ry='2' />
      <path d='M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2' />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2' />
      <circle cx='9' cy='7' r='4' />
      <path d='M22 21v-2a4 4 0 0 0-3-3.87' />
      <path d='M16 3.13a4 4 0 0 1 0 7.75' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <rect width='18' height='18' x='3' y='4' rx='2' ry='2' />
      <line x1='16' x2='16' y1='2' y2='6' />
      <line x1='8' x2='8' y1='2' y2='6' />
      <line x1='3' x2='21' y1='10' y2='10' />
    </svg>
  );
}

function MapPinIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z' />
      <circle cx='12' cy='10' r='3' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns='http://www.w3.org/2000/svg'
      width='18'
      height='18'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='10' />
      <line x1='15' x2='9' y1='9' y2='15' />
      <line x1='9' x2='15' y1='9' y2='15' />
    </svg>
  );
}

interface CreateProofOfDeliveryFormProps {
  selectedOutboundProcess: OutboundProcess;
  onClose: () => void;
  onSubmit: (values: CreateProofOfDeliveryFormValues) => void;
  isDesktop: boolean;
  isTablet: boolean;
}

export function CreateProofOfDeliveryForm({
  selectedOutboundProcess,
  onClose,
  onSubmit,
  isDesktop,
  isTablet,
}: CreateProofOfDeliveryFormProps) {
  const {
    handleSubmit,
    control,
    setValue,
    isSubmitting,
    isValid,
    fields: skusFields,
    watch,
  } = useReactHookFormWithFieldArray<CreateProofOfDeliveryFormValues>({
    defaultValues: makeDefaultValues(selectedOutboundProcess),
    validationSchema,
    fieldArrayName: 'skus',
  });

  const { user } = useAuth();

  if (!selectedOutboundProcess) {
    return null;
  }

  const { shipmentNoteNumber, customerName } =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    selectedOutboundProcess.shipmentNote!;

  const podNumber = shipmentNoteNumber.replace('-SN', '-POD');

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(podNumber);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col'>
      {/* Document Reference Badge */}
      <div className='px-6 pt-2 pb-4'>
        <div className='inline-flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-md border border-gray-200'>
          <span className='text-sm font-medium text-gray-700'>
            Proof of Delivery #{podNumber}
          </span>
          <button
            type='button'
            onClick={handleCopyToClipboard}
            className='text-gray-500 hover:text-gray-700 transition-colors'
            aria-label='Copy POD number'
          >
            <CopyIcon />
          </button>
        </div>
      </div>

      {/* Metadata Fields */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Recipient name */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Recipient name</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {customerName}
          </span>
        </div>

        {/* Recipient address */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <MapPinIcon />
            <span className='text-xs font-medium'>Recipient address</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {selectedOutboundProcess.despatchNote?.customer?.fullAddress ??
              'N/A'}
          </span>
        </div>

        {/* Signed off by */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <UserIcon />
            <span className='text-xs font-medium'>Signed off by</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {user?.name}
          </span>
        </div>

        {/* Required ETA */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Required ETA</span>
          </div>
          <span className='text-sm font-medium text-gray-900'>
            {`${dayjs(
              selectedOutboundProcess.despatchNote?.requiredTimeOfArrival
                .dateTime
            )
              .tz(
                selectedOutboundProcess.despatchNote?.requiredTimeOfArrival
                  .timeZone
              )
              .format('D MMM YYYY')} · by ${dayjs(
              selectedOutboundProcess.despatchNote?.requiredTimeOfArrival
                .dateTime
            )
              .tz(
                selectedOutboundProcess.despatchNote?.requiredTimeOfArrival
                  .timeZone
              )
              .format('HH:mm')}`}
          </span>
        </div>
      </div>

      {/* Second row of metadata */}
      <div
        className={`grid gap-4 px-6 py-4 border-b border-gray-200 ${
          isDesktop ? 'grid-cols-4' : isTablet ? 'grid-cols-2' : 'grid-cols-1'
        }`}
      >
        {/* Actual Delivery Date - Editable */}
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-1.5 text-gray-500 mb-1'>
            <CalendarIcon />
            <span className='text-xs font-medium'>Actual Delivery Date</span>
          </div>
          <Controller
            name='actualDeliveryDate'
            control={control}
            render={({ field, fieldState: { error } }) => {
              return (
                <Datepicker
                  name={field.name}
                  value={
                    field.value
                      ? dayjs(field.value).format('YYYY-MM-DD')
                      : dayjs().format('YYYY-MM-DD')
                  }
                  onChange={(e) => {
                    setValue(
                      'actualDeliveryDate',
                      dayjs(e.target.value).toISOString(),
                      {
                        shouldValidate: true,
                      }
                    );
                  }}
                  startDate={dayjs().format('YYYY-MM-DD')}
                  isInvalid={!!error}
                  validationMessage={error?.message}
                />
              );
            }}
          />
        </div>
      </div>

      {/* SKU Table */}
      <div className='px-6 py-4'>
        <h3 className='text-sm font-semibold text-gray-900 mb-4'>
          SKU / items
        </h3>

        {/* Table Header - Desktop only */}
        {isDesktop && (
          <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1fr_1.5fr] gap-4 pb-3 border-b border-gray-200 mb-4'>
            <span className='text-xs font-semibold text-gray-500 text-center'>
              #
            </span>
            <span className='text-xs font-semibold text-gray-500'>SKU ID</span>
            <span className='text-xs font-semibold text-gray-500'>
              SKU name
            </span>
            <span className='text-xs font-semibold text-gray-500'>UoM</span>
            <span className='text-xs font-semibold text-gray-500'>
              Despatched SKUs
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Delivered SKUs
            </span>
            <span className='text-xs font-semibold text-gray-500'>
              Delivered Pallets
            </span>
          </div>
        )}

        {/* SKU Rows */}
        <div className='flex flex-col gap-6'>
          {skusFields.map((skusField, index) => {
            const despatchedQuantity = watch(
              `skus.${index}.despatchedQuantity`
            );
            const receivedSkuQuantity = watch(
              `skus.${index}.receivedSkuQuantity`
            );

            const isDiscrepancy =
              receivedSkuQuantity !== undefined &&
              despatchedQuantity !== receivedSkuQuantity;

            const hasPackagingInfo = skusField.packagingDetails.packagingInfo;

            let amountOfSkusInLayer: number | undefined;
            let amountOfLayersInPallet: number | undefined;
            let packagingInfoType: string | undefined;
            let palletisingType: string | null | undefined;

            if (hasPackagingInfo) {
              amountOfSkusInLayer =
                skusField.packagingDetails.packagingInfo?.amountOfSkusInLayer;

              amountOfLayersInPallet =
                skusField.packagingDetails.packagingInfo
                  ?.amountOfLayersInPallet;

              packagingInfoType =
                skusField.packagingDetails.packagingInfo?.type;
            } else {
              palletisingType = skusField.packagingDetails.palletisingType;
            }

            return (
              <div
                key={skusField.id}
                className={`${
                  index !== skusFields.length - 1
                    ? 'pb-6 border-b border-gray-100'
                    : ''
                }`}
              >
                {isDesktop ? (
                  /* Desktop Layout */
                  <div className='flex flex-col gap-4'>
                    <div className='grid grid-cols-[2.5rem_1fr_1.5fr_1fr_1fr_1fr_1.5fr] gap-4 items-start'>
                      {/* Index */}
                      <div className='flex justify-center'>
                        <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                          {index + 1}
                        </span>
                      </div>
                      {/* SKU ID */}
                      <span className='text-sm text-gray-900'>
                        {skusField.skuIdentifier}
                      </span>
                      {/* SKU name */}
                      <span className='text-sm text-gray-900'>
                        {skusField.skuName}
                      </span>
                      {/* UoM */}
                      <span className='text-sm text-gray-900'>
                        {skusField.skuUnitOfMeasure}
                      </span>
                      {/* Despatched quantity */}
                      <span className='text-sm text-gray-900'>
                        {skusField.despatchedQuantity}
                      </span>
                      {/* Delivered SKU Quantity */}
                      <Controller
                        name={`skus.${index}.receivedSkuQuantity`}
                        control={control}
                        render={({
                          field,
                          fieldState: { error, isTouched },
                        }) => (
                          <Input
                            name={field.name}
                            type='number'
                            value={field.value?.toString() ?? ''}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              const receivedSkuQuantityValue = parseInt(
                                e.target.value,
                                10
                              );

                              setValue(
                                `skus.${index}.receivedSkuQuantity`,
                                receivedSkuQuantityValue,
                                {
                                  shouldValidate: true,
                                }
                              );

                              if (
                                receivedSkuQuantityValue === despatchedQuantity
                              ) {
                                setValue(`skus.${index}.discrepancyReason`, '');
                              }
                            }}
                            isInvalid={Boolean(error && isTouched)}
                            validationMessage={error?.message}
                            sizeVariant='sm'
                          />
                        )}
                      />
                      {/* Delivered Pallet Quantity */}
                      <span className='text-sm text-gray-900 whitespace-nowrap'>
                        {hasPackagingInfo ? (
                          receivedSkuQuantity ? (
                            <PalletCalculator
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              palletName={packagingInfoType!}
                              skuQuantity={receivedSkuQuantity}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              amountOfSkusInLayer={amountOfSkusInLayer!}
                              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                              amountOfLayersInPallet={amountOfLayersInPallet!}
                            />
                          ) : (
                            <span>&nbsp;</span>
                          )
                        ) : (
                          palletisingType
                        )}
                      </span>
                    </div>

                    {/* Discrepancy reason row */}
                    {isDiscrepancy && (
                      <div className='grid grid-cols-[2.5rem_1fr] gap-4'>
                        <div />
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => (
                            <Textarea
                              name={field.name}
                              label='Discrepancy reason'
                              value={field.value ?? ''}
                              onChange={(
                                e: ChangeEvent<HTMLTextAreaElement>
                              ) => {
                                field.onChange(e.target.value);
                              }}
                              onBlur={field.onBlur}
                              isInvalid={Boolean(error && isTouched)}
                              validationMessage={error?.message}
                              placeholder='Explain the discrepancy...'
                            />
                          )}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  /* Mobile/Tablet Layout */
                  <div className='flex gap-3'>
                    {/* Index */}
                    <div className='flex-shrink-0'>
                      <span className='inline-flex items-center justify-center w-7 h-7 text-xs font-medium text-gray-700 bg-gray-100 rounded-full'>
                        {index + 1}
                      </span>
                    </div>
                    {/* Details */}
                    <div className='flex-1 flex flex-col gap-4'>
                      <div
                        className={`grid gap-3 ${
                          isTablet ? 'grid-cols-2' : 'grid-cols-1'
                        }`}
                      >
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU ID
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.skuIdentifier}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            SKU name
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.skuName}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            UoM
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.skuUnitOfMeasure}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Despatched SKUs
                          </span>
                          <span className='text-sm text-gray-900'>
                            {skusField.despatchedQuantity}
                          </span>
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Delivered SKUs
                          </span>
                          <Controller
                            name={`skus.${index}.receivedSkuQuantity`}
                            control={control}
                            render={({
                              field,
                              fieldState: { error, isTouched },
                            }) => (
                              <Input
                                name={field.name}
                                type='number'
                                value={field.value?.toString() ?? ''}
                                onChange={(
                                  e: ChangeEvent<HTMLInputElement>
                                ) => {
                                  const receivedSkuQuantityValue = parseInt(
                                    e.target.value,
                                    10
                                  );

                                  setValue(
                                    `skus.${index}.receivedSkuQuantity`,
                                    receivedSkuQuantityValue,
                                    {
                                      shouldValidate: true,
                                    }
                                  );

                                  if (
                                    receivedSkuQuantityValue ===
                                    despatchedQuantity
                                  ) {
                                    setValue(
                                      `skus.${index}.discrepancyReason`,
                                      ''
                                    );
                                  }
                                }}
                                isInvalid={Boolean(error && isTouched)}
                                validationMessage={error?.message}
                                sizeVariant='sm'
                              />
                            )}
                          />
                        </div>
                        <div className='flex flex-col gap-0.5'>
                          <span className='text-xs font-medium text-gray-500'>
                            Delivered Pallets
                          </span>
                          <span className='text-sm text-gray-900'>
                            {hasPackagingInfo ? (
                              receivedSkuQuantity ? (
                                <PalletCalculator
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  palletName={packagingInfoType!}
                                  skuQuantity={receivedSkuQuantity}
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  amountOfSkusInLayer={amountOfSkusInLayer!}
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                  amountOfLayersInPallet={
                                    amountOfLayersInPallet!
                                  }
                                />
                              ) : (
                                <span>&nbsp;</span>
                              )
                            ) : (
                              palletisingType
                            )}
                          </span>
                        </div>
                      </div>

                      {/* Discrepancy reason */}
                      {isDiscrepancy && (
                        <Controller
                          name={`skus.${index}.discrepancyReason`}
                          control={control}
                          render={({
                            field,
                            fieldState: { error, isTouched },
                          }) => (
                            <Textarea
                              name={field.name}
                              label='Discrepancy reason'
                              value={field.value ?? ''}
                              onChange={(
                                e: ChangeEvent<HTMLTextAreaElement>
                              ) => {
                                field.onChange(e.target.value);
                              }}
                              onBlur={field.onBlur}
                              isInvalid={Boolean(error && isTouched)}
                              validationMessage={error?.message}
                              placeholder='Explain the discrepancy...'
                            />
                          )}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comment Section */}
      <div className='px-6 py-4 border-t border-gray-200'>
        <Controller
          name='comment'
          control={control}
          render={({ field, fieldState: { error, isTouched } }) => (
            <Textarea
              {...field}
              value={field.value ?? ''}
              label='Comment'
              optional
              isInvalid={Boolean(error && isTouched)}
              validationMessage={error?.message}
              placeholder='Type here...'
            />
          )}
        />
      </div>

      {/* Footer Actions */}
      <div
        className={`flex gap-3 px-6 py-4 mt-4 border-t border-gray-200 ${
          isDesktop ? 'flex-row justify-end' : 'flex-col-reverse'
        }`}
      >
        <Button
          label='Discard'
          intent='secondary'
          size='sm'
          onClick={onClose}
          disabled={isSubmitting}
          startIcon={<CloseIcon />}
          className={!isDesktop ? 'w-full' : ''}
          type='button'
        />
        <Button
          label={isSubmitting ? 'Submitting...' : 'Save'}
          intent='primary'
          size='sm'
          type='submit'
          disabled={isSubmitting || !isValid}
          className={!isDesktop ? 'w-full' : ''}
        />
      </div>
    </form>
  );
}
